import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Connection Quality (CQ) - modul nou, numai in v4.
 *
 *   GET  /devices/{id}/connection-quality?range=1h|4h|24h|7d|30d   ecranul de pe fisa
 *   GET  /devices/{id}/connection-quality?from=..&to=..       zoom (`2026-09-30T10:05`, 5 min - 30 zile)
 *   GET  /devices/{id}/connection-quality/settings            cardul din Agent Settings -> Functions
 *   POST /devices/{id}/connection-quality/settings            mode, target, interval_s
 *   GET  /settings/connection-quality                         implicitul firmei (Install Options)
 *   POST /settings/connection-quality                         enabled, target, interval_s
 *   GET  /devices/{id}/connection-quality/path?trace=N        sectiunea Path (traseele)
 *   POST /devices/{id}/connection-quality/traceroute          comanda CQT (409 = una in asteptare)
 *   GET  /easeedesk/tickets/{id}/connection-quality           legatura dinaintea tichetului
 *   GET  /settings/connection-quality/access-points           AP-urile Wi-Fi ale firmei (faza 3)
 *   POST /settings/connection-quality/access-points           keys[], name, office_id
 *   GET  /devices/{id}/connection-quality/speed               sectiunea Speed (faza 4)
 *   POST /devices/{id}/connection-quality/speedtest           comanda CQS (409 = una in asteptare / CQ stins)
 *
 * `wifi_diag` ("Allow Wi-Fi diagnostics") se trimite la ambele salvari de
 * setari; lipsa lui lasa bifa cum era (formular vechi). La fel `speed_auto`
 * ("Daily speed test") si `speed_hour` (ora locala a aparatului, 0-23).
 *
 * Un camp gresit raspunde 422 cu `{error: 'invalid', field, message}`.
 */

export type CqMode = 'inherit' | 'on' | 'off'
export type CqRange = '1h' | '4h' | '24h' | '7d' | '30d'

export type CqEffective = {
  enabled: boolean
  target: string
  interval_s: number
  /** De unde vine: randul aparatului, al firmei, sau nimic scris (oprit). */
  from: 'device' | 'company' | 'default'
  /** "Allow Wi-Fi diagnostics" care se aplica (faza 3). */
  wifi_diag?: boolean
  /** "Daily speed test" si ora lui (faza 4). */
  speed_auto?: boolean
  speed_hour?: number
}

export type CqDeviceSettings = {
  device: { id: number }
  mode: CqMode
  /** null = tinta implicita (`defaultTarget`). */
  target: string | null
  interval_s: number
  /** Bifa din campuri: a aparatului, sau a firmei cand mosteneste. */
  wifi_diag?: boolean
  /** "Daily speed test": al aparatului, sau al firmei cand mosteneste. */
  speed_auto?: boolean
  speed_hour?: number
  company: { enabled: boolean; target: string; interval_s: number; wifi_diag?: boolean; speed_auto?: boolean; speed_hour?: number }
  effective: CqEffective
  defaultTarget: string
  limits: { min: number; max: number }
  /** Pagina Microsoft despre locatie si Wi-Fi in Windows 11. */
  locationDoc?: string
  /** Ce face testul zilnic (banda, intarzierea, conexiunile cu cost). */
  speedHelp?: string
  canEdit: boolean
}

export type CqCompanySettings = {
  enabled: boolean
  target: string | null
  interval_s: number
  wifi_diag?: boolean
  speed_auto?: boolean
  speed_hour?: number
  defaultTarget: string
  limits: { min: number; max: number }
  locationDoc?: string
  speedHelp?: string
  canWrite: boolean
}

export type CqLinePoint = {
  bucket: string
  avg: number | null
  min: number | null
  max: number | null
  loss_pct: number | null
  jitter: number | null
  mos: number | null
  via: string
}

export type CqHttpPoint = {
  bucket: string
  dns_ms: number | null
  tcp_ms: number | null
  tls_ms: number | null
  ttfb_ms: number | null
  total_ms: number | null
  requests: number
  errors: number
}

export type CqTcpPoint = {
  bucket: string
  retrans_pct: number | null
  segs_out: number
  retrans: number
}

export type CqVerdictLine = {
  sent: number
  loss_pct: number | null
  rtt_avg: number | null
  jitter: number | null
  bad: boolean
  /** Routerul nu raspunde la ping, dar traficul trece (Verdict::routerSilent). */
  silent?: boolean
  reason: string
}

export type CqVerdict = {
  code: 'ok' | 'local' | 'isp' | 'target' | 'no_data'
  text: string
  why: string
  /** Note care nu schimba concluzia - "Router does not answer ping ...". */
  notes?: string[]
  lines: Partial<Record<'gateway' | 'isp' | 'target', CqVerdictLine>>
}

export type CqBaseline = {
  ready: boolean
  points: number
  median: number | null
  mad: number | null
  threshold: number | null
  current: number | null
  above: boolean
  kind?: string
}

export type CqPage = {
  device: { id: number; name?: string | null }
  /** `custom` = zoom (`from` / `to` alese). */
  range: CqRange | 'custom'
  ranges: CqRange[]
  bucket_s: number
  from: string
  to: string
  settings: CqEffective
  canEditSettings: boolean
  lastSample: string | null
  thresholds: { window_minutes: number; loss_bad_pct: number; latency_bad_ms: Record<string, number> }
  verdict: CqVerdict
  baseline: { target: CqBaseline | null; gateway: CqBaseline | null }
  latency: { gateway: CqLinePoint[]; isp: CqLinePoint[]; target: CqLinePoint[] }
  /** Routerul n-a raspuns la niciun ping in perioada, dar restul da. */
  gatewaySilent: boolean
  http: CqHttpPoint[]
  tcp: CqTcpPoint[]
  checks: CqCheck[]
  compare: CqCompare | null
  traceMarks: CqTraceMark[]
  /** Sectiunea Wi-Fi; null/lipsa = aparatul n-are date Wi-Fi in perioada. */
  wifi?: CqWifi | null
}

export type CqSignal = 'good' | 'fair' | 'weak' | 'poor'

export type CqWifiCurrent = {
  at: string
  iface: string | null
  ssid: string | null
  bssid: string | null
  band: string | null
  channel: number | null
  width_mhz: number | null
  phy: string | null
  rssi_dbm: number | null
  signal_pct: number | null
  noise_dbm: number | null
  rx_mbps: number | null
  tx_mbps: number | null
  retry_pct: number | null
  fail_pct: number | null
  caps_5g: boolean | null
  bssid_ok: boolean
  signal: CqSignal | null
}

export type CqWifiPoint = {
  bucket: string
  rssi: number | null
  rssi_min: number | null
  rssi_max: number | null
  rx_mbps: number | null
  tx_mbps: number | null
  retry_pct: number | null
}

export type CqWifiEvent = {
  at: string
  kind: 'connect' | 'disconnect' | 'roam' | 'fail'
  ssid: string | null
  bssid_from: string | null
  bssid_to: string | null
  reason: string | null
  rssi_dbm: number | null
  ap_from: string | null
  ap_to: string | null
}

export type CqWifiMessage = {
  code: 'bssid_unavailable' | 'weak_signal' | 'band_24' | 'retries' | 'ping_pong'
  level: 'info' | 'warn'
  text: string
}

export type CqApCompare = {
  code: 'ap_all' | 'ap_some' | 'ap_only' | 'ap_not_enough'
  text: string
  affected: number
  measured: number
  ap: string
}

export type CqWifiAp = { name: string | null; label: string; bssid: string | null }

/**
 * Un AP fizic la care a stat aparatul in perioada afisata (DeviceAps.php).
 * `floor` = etajul pe care e pus AP-ul; null cand nu e pus, sau omul nu vede planurile.
 */
export type CqDeviceAp = {
  key: string
  prefix: string
  prefixes: string[]
  name: string | null
  label: string
  bssids: string[]
  office: string | null
  minutes: number
  pct: number
  rssi_median: number | null
  rssi_min: number | null
  retry_median: number | null
  first: string
  last: string
  weak: boolean
  sticky: boolean
  current: boolean
  floor: { id: number; name: string } | null
}

export type CqWifi = {
  current: CqWifiCurrent
  /** AP-ul curent: numele dat de om (`name`), altfel BSSID-ul (`label`). */
  ap: CqWifiAp | null
  window: { retry_pct: number | null; rssi: number | null; minutes: number }
  series: CqWifiPoint[]
  events: CqWifiEvent[]
  messages: CqWifiMessage[]
  compare: CqApCompare | null
  /** AP-urile perioadei, cel mai mult stat intai (lipsa la raspunsurile vechi). */
  aps?: CqDeviceAp[]
  thresholds: { good: number; fair: number; poor: number; retry_high_pct: number }
  wifiDiag: boolean
  locationDoc: string
}

/** Starea Wi-Fi din instantaneul tichetului. */
export type CqWifiTicket = {
  at: string
  ssid: string | null
  band: string | null
  channel: number | null
  rssi_dbm: number | null
  signal: CqSignal | null
  retry_pct: number | null
  bssid_ok: boolean
  ap: CqWifiAp | null
  disconnects: number
  roams: number
}

export type CqApDevice = { id: number; name: string; minutes: number; rssi: number | null; retry_pct: number | null; last: string }

export type CqAccessPoint = {
  key: string
  /** Cheile grupurilor unite sub acelasi nume; salvarea le trimite pe toate. */
  keys: string[]
  name: string | null
  office_id: number | null
  bssids: { bssid: string; seen: boolean }[]
  clients: number
  devices: CqApDevice[]
  median_rssi: number | null
  median_retry_pct: number | null
  weak: boolean
  sticky: { id: number; name: string; rssi: number | null }[]
}

export type CqAccessPointsPage = {
  hours: number
  aps: CqAccessPoint[]
  offices: { id: number; name: string }[]
  thresholds: { weak_rssi: number; weak_retry_pct: number; sticky_rssi: number; sticky_minutes: number }
  canWrite: boolean
}

export type CqCheck = {
  kind: 'mtu' | 'dns' | 'captive' | 'vpn'
  name: string
  state: 'ok' | 'warn' | 'info' | 'none'
  text: string
  at: string | null
}

export type CqCompare = {
  code: 'whole_office' | 'some_devices' | 'only_this' | 'not_enough' | 'not_needed'
  text: string
  affected: number
  measured: number
  scope: 'office' | 'subnet' | 'public_ip' | null
}

export type CqTraceReason = 'manual' | 'periodic' | 'anomaly'

export type CqTraceMark = { id: number; at: string; reason: CqTraceReason; changed: boolean }

export type CqTraceItem = {
  id: number
  at: string
  target: string | null
  reason: CqTraceReason
  hop_count: number
  reached: boolean
  path_hash: string | null
  changed: boolean
}

export type CqHopState = 'ok' | 'bad' | 'limited' | 'silent'

export type CqHop = {
  ttl: number
  ip: string | null
  host: string | null
  sent: number
  lost: number
  loss_pct: number | null
  rtt_min: number | null
  rtt_avg: number | null
  rtt_max: number | null
  jitter: number | null
  state: CqHopState
  /** Hopul unde incepe problema care continua pana la tinta. */
  origin: boolean
  note: string
}

export type CqTraceDetail = {
  id: number
  at: string
  target: string | null
  reason: CqTraceReason
  reached: boolean
  hop_count: number
  path_hash: string | null
  changed: boolean
  summary: string
  hops: CqHop[]
}

export type CqRunState = { state: 'idle' | 'pending' | 'running'; since: string | null }

export type CqPathPage = {
  device: { id: number }
  enabled: boolean
  target: string
  traces: CqTraceItem[]
  trace: CqTraceDetail | null
  run: CqRunState
  canRun: boolean
}

export type CqSpeedGrade = 'A' | 'B' | 'C' | 'D' | 'F'

/** Un test de viteza (`cq_speed`), cu cresterea latentei sub sarcina si nota. */
export type CqSpeedTest = {
  id: number
  at: string
  reason: 'manual' | 'scheduled'
  server: string | null
  streams: number | null
  down_mbps: number | null
  up_mbps: number | null
  bytes_down: number | null
  bytes_up: number | null
  idle_ms: number | null
  loaded_down_ms: number | null
  loaded_up_ms: number | null
  rpm: number | null
  duration_s: number | null
  error: string | null
  bloat_ms: number | null
  grade: CqSpeedGrade | null
}

export type CqSpeedPoint = { at: string; down_mbps: number | null; up_mbps: number | null; bloat_ms: number | null; error: boolean }

export type CqSpeedPage = {
  device: { id: number }
  enabled: boolean
  auto: boolean
  hour: number
  from: string
  to: string
  bucket_s: number
  /** Cel mai nou test, reusit sau nu. */
  last: CqSpeedTest | null
  /** Cel mai nou test reusit. */
  lastGood: CqSpeedTest | null
  history: CqSpeedPoint[]
  recent: CqSpeedTest[]
  /** Pragurile notei, in ms: sub prag = nota; peste D = F. */
  grades: Record<'A' | 'B' | 'C' | 'D', number>
  explain: string
  help: string
  run: CqRunState
  canRun: boolean
}

export type CqTicketPage =
  | { available: false }
  | {
      available: true
      source: 'snapshot' | 'live'
      taken_at: string | null
      device: { id: number; name: string }
      ticket: { id: number; created: string }
      from: string
      to: string
      bucket_s: number
      verdict: CqVerdict
      compare: CqCompare | null
      latency: { gateway: CqLinePoint[]; isp: CqLinePoint[]; target: CqLinePoint[] }
      gatewaySilent: boolean
      trace: CqTraceDetail | null
      checks: CqCheck[]
      /** Starea Wi-Fi dinaintea tichetului; null = pe cablu (instantaneele vechi n-o au). */
      wifi?: CqWifiTicket | null
      /** Ultimul test de viteza reusit din cele 7 zile dinainte (faza 4). */
      speed?: CqSpeedTest | null
    }

/**
 * Cu `zoom`, serverul aduce intervalul ales in locul perioadei. Perioadele
 * scurte (pana la 24h) se reimprospateaza la minut; zoom-ul e un interval
 * fix, deci nu. Datele vechi raman pe ecran pana vin cele noi - un zoom nu
 * clipeste graficele.
 */
export function connectionQualityQuery(id: number, range: CqRange, zoom?: { from: string; to: string } | null) {
  const query = zoom
    ? `from=${encodeURIComponent(zoom.from)}&to=${encodeURIComponent(zoom.to)}`
    : `range=${range}`
  return queryOptions({
    queryKey: ['device', id, 'connection-quality', range, zoom?.from ?? null, zoom?.to ?? null],
    queryFn: () => apiGet<CqPage>(`/devices/${id}/connection-quality?${query}`),
    staleTime: 30_000,
    refetchInterval: !zoom && (range === '1h' || range === '4h' || range === '24h') ? 60_000 : false,
    placeholderData: (previous) => (previous?.device.id === id ? previous : undefined),
  })
}

export function cqDeviceSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'connection-quality', 'settings'],
    queryFn: () => apiGet<CqDeviceSettings>(`/devices/${id}/connection-quality/settings`),
    staleTime: 0,
  })
}

export function saveCqDeviceSettings(
  id: number,
  fields: { mode: CqMode; target: string; interval_s: number; wifi_diag?: boolean; speed_auto?: boolean; speed_hour?: number },
) {
  const { wifi_diag, speed_auto, speed_hour, ...rest } = fields
  return apiPost<CqDeviceSettings>(`/devices/${id}/connection-quality/settings`, {
    ...rest,
    ...(wifi_diag === undefined ? {} : { wifi_diag: wifi_diag ? '1' : '0' }),
    ...(speed_auto === undefined ? {} : { speed_auto: speed_auto ? '1' : '0' }),
    ...(speed_hour === undefined ? {} : { speed_hour }),
  })
}

export const cqCompanySettingsQuery = queryOptions({
  queryKey: ['settings', 'connection-quality'],
  queryFn: () => apiGet<CqCompanySettings>('/settings/connection-quality'),
})

export function saveCqCompanySettings(fields: {
  enabled: boolean
  target: string
  interval_s: number
  wifi_diag?: boolean
  speed_auto?: boolean
  speed_hour?: number
}) {
  return apiPost<CqCompanySettings>('/settings/connection-quality', {
    enabled: fields.enabled ? '1' : '0',
    target: fields.target,
    interval_s: fields.interval_s,
    ...(fields.wifi_diag === undefined ? {} : { wifi_diag: fields.wifi_diag ? '1' : '0' }),
    ...(fields.speed_auto === undefined ? {} : { speed_auto: fields.speed_auto ? '1' : '0' }),
    ...(fields.speed_hour === undefined ? {} : { speed_hour: fields.speed_hour }),
  })
}

export const cqAccessPointsQuery = queryOptions({
  queryKey: ['settings', 'connection-quality', 'access-points'],
  queryFn: () => apiGet<CqAccessPointsPage>('/settings/connection-quality/access-points'),
  staleTime: 60_000,
})

export function saveCqAccessPoint(fields: { keys: string[]; name: string; office_id: number | null }) {
  return apiPost<CqAccessPointsPage>('/settings/connection-quality/access-points', {
    keys: fields.keys,
    name: fields.name,
    office_id: fields.office_id ?? '',
  })
}

export function cqPathQuery(id: number, trace: number | null) {
  return queryOptions({
    queryKey: ['device', id, 'connection-quality', 'path', trace ?? 'latest'],
    queryFn: () => apiGet<CqPathPage>(`/devices/${id}/connection-quality/path${trace ? `?trace=${trace}` : ''}`),
    staleTime: 15_000,
    /* Cat asteapta un traseu cerut, se intreaba des; altfel o data pe minut. */
    refetchInterval: (q) => (q.state.data && q.state.data.run.state !== 'idle' ? 5_000 : 60_000),
  })
}

export function runTraceroute(id: number) {
  return apiPost<CqPathPage>(`/devices/${id}/connection-quality/traceroute`, {})
}

export function cqSpeedQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'connection-quality', 'speed'],
    queryFn: () => apiGet<CqSpeedPage>(`/devices/${id}/connection-quality/speed`),
    staleTime: 15_000,
    /* Cat asteapta un test cerut, se intreaba des; altfel o data la cinci minute. */
    refetchInterval: (q) => (q.state.data && q.state.data.run.state !== 'idle' ? 5_000 : 300_000),
  })
}

export function runSpeedTest(id: number) {
  return apiPost<CqSpeedPage>(`/devices/${id}/connection-quality/speedtest`, {})
}

export function ticketConnectionQualityQuery(ticketId: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticketId, 'connection-quality'],
    queryFn: () => apiGet<CqTicketPage>(`/easeedesk/tickets/${ticketId}/connection-quality`),
    staleTime: 5 * 60_000,
    retry: false,
  })
}
